import { useState, useEffect, useCallback } from 'react';

/**
 * useSlideNavigation - Hook for keyboard-navigable slide presentations
 *
 * @param {number} totalSlides - Total number of slides
 * @param {function} onExit - Callback when user presses Escape
 * @returns {object} Navigation state and controls
 */
export function useSlideNavigation(totalSlides, onExit) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    }
  }, [currentSlide, isAnimating, totalSlides]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(prev => prev - 1);
        setIsAnimating(false);
      }, 300);
    }
  }, [currentSlide, isAnimating]);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < totalSlides && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsAnimating(false);
      }, 300);
    }
  }, [isAnimating, totalSlides]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape' && onExit) onExit();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextSlide, prevSlide, onExit]);

  const progress = ((currentSlide + 1) / totalSlides) * 100;
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === totalSlides - 1;

  return {
    currentSlide,
    isAnimating,
    progress,
    isFirstSlide,
    isLastSlide,
    nextSlide,
    prevSlide,
    goToSlide,
  };
}
